/**
 * GHLAuthGate — wraps all /app/* routes.
 *
 * - While auth is loading: shows spinner
 * - If authenticated as staff: renders children
 * - If not authenticated: shows "open from seeUwell" message
 *
 * Iframe detection: if not inside a GHL iframe and no ssoToken is present,
 * the user opened the URL directly — show the access message.
 */

import React from 'react';
import { useAuth } from '../lib/auth-context.js';
import { isGHLIframe } from '../lib/ghl-sso.js';

interface Props {
  children: React.ReactNode;
}

export default function GHLAuthGate({ children }: Props): React.ReactElement {
  const { loading, error, isStaff } = useAuth();

  if (loading) {
    return (
      <div style={styles.center}>
        <div style={styles.spinner} />
        <p style={styles.muted}>Connecting to seeUwell…</p>
      </div>
    );
  }

  if (isStaff) {
    return <>{children}</>;
  }

  // Not authenticated — show access message
  const inIframe = isGHLIframe();

  return (
    <div style={styles.center}>
      <div style={styles.card}>
        <div style={styles.lockIcon}>🔒</div>
        <h2 style={styles.title}>ClearIntake</h2>
        {error ? (
          <>
            <p style={styles.body}>Authentication failed. Please refresh and try again.</p>
            <p style={styles.errorDetail}>{error}</p>
          </>
        ) : (
          <>
            <p style={styles.body}>
              Please open ClearIntake from your seeUwell menu.
            </p>
            <p style={styles.sub}>
              This app must be accessed through the seeUwell sidebar or contact actions.
            </p>
            <a
              href="https://account.seeuwell.com"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.link}
            >
              Open seeUwell →
            </a>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles: Record<string, React.CSSProperties> = {
  center: {
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    height:         '100vh',
    background:     '#f9fafb',
    fontFamily:     "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  card: {
    background:   '#fff',
    borderRadius: 12,
    padding:      '40px 48px',
    textAlign:    'center',
    boxShadow:    '0 4px 24px rgba(0,0,0,0.08)',
    maxWidth:     360,
    width:        '100%',
  },
  lockIcon: { fontSize: 40, marginBottom: 12 },
  title: { margin: '0 0 16px', fontSize: 22, fontWeight: 700, color: '#111827' },
  body: { margin: '0 0 8px', fontSize: 15, color: '#374151', lineHeight: 1.6 },
  sub: { margin: '0 0 24px', fontSize: 13, color: '#9ca3af', lineHeight: 1.5 },
  errorDetail: { margin: '8px 0 0', fontSize: 12, color: '#ef4444' },
  link: {
    display:         'inline-block',
    background:      '#2563eb',
    color:           '#fff',
    borderRadius:    8,
    padding:         '10px 24px',
    textDecoration:  'none',
    fontSize:        14,
    fontWeight:      500,
  },
  spinner: {
    width:       32,
    height:      32,
    border:      '3px solid #e5e7eb',
    borderTop:   '3px solid #2563eb',
    borderRadius: '50%',
    animation:   'spin 0.8s linear infinite',
    marginBottom: 12,
  },
  muted: { color: '#9ca3af', fontSize: 14 },
};
